// ══════════════════════════════════════════════════════════════════
//  Papel do usuário na Lista de vidas (cs | admin | nenhum)
// ══════════════════════════════════════════════════════════════════
//  Quem responde é o servidor (`meu-papel`, relido do banco). Cache por
//  USUÁRIO, em memória — nunca em storage. Esconder menu/rota é
//  conveniência: toda ação do servidor confere o papel de novo.
//
//  ERRO não é "sem papel": falha de rede ou `auth_indisponivel` deixam
//  `papel` nulo mas preenchem `erro`. Só `data.papel === null` (resposta do
//  servidor) significa que a pessoa não tem acesso.
//
//  `limites24h` são os tetos do Hub por empresa em 24h (informativo na tela;
//  quem recusa é o Hub com 429).
// ══════════════════════════════════════════════════════════════════

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import {
  meuPapelListaVidas, ListaVidasGatewayError, type Papel, type TipoOperacao,
} from '@app/listaVidas';

export const CHAVE_PAPEL = 'listaVidasPapel';

export interface UseListaVidasPapel {
  papel: Papel | null;
  loading: boolean;
  configurado: boolean;
  /** Tetos por operação em 24h; null enquanto o servidor não respondeu. */
  limites24h: Record<TipoOperacao, number> | null;
  /** Falha ao descobrir o papel (rede, servidor, auth indisponível). */
  erro: unknown;
  /** O servidor respondeu e a pessoa não tem papel. */
  semPapel: boolean;
  refetch: () => void;
}

export const useListaVidasPapel = (): UseListaVidasPapel => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [CHAVE_PAPEL, user?.id || ''],
    queryFn: meuPapelListaVidas,
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  return {
    papel: query.data?.papel ?? null,
    loading: query.isLoading,
    configurado: query.data?.configurado ?? true,
    limites24h: query.data?.limites24h ?? null,
    erro: query.isError && !query.data ? query.error : null,
    semPapel: !!query.data && query.data.papel === null,
    refetch: () => { void query.refetch(); },
  };
};

/** 403 por papel: o que a tela sabia sobre o papel ficou velho. */
export const papelMudou = (err: unknown): boolean =>
  err instanceof ListaVidasGatewayError
  && err.status === 403
  && (err.codigo === 'sem_papel' || err.codigo === 'papel_insuficiente');

/** Devolve um callback para chamar no `onError` de qualquer ação: se o erro
 *  for 403 de papel, invalida o papel em cache (menu e rota reavaliam). */
export const useRevisarPapel = (): ((err: unknown) => void) => {
  const qc = useQueryClient();
  return useCallback((err: unknown) => {
    if (papelMudou(err)) void qc.invalidateQueries({ queryKey: [CHAVE_PAPEL] });
  }, [qc]);
};

/** Mesmo efeito para o `error` de uma query. */
export const useRevisarPapelNoErro = (erro: unknown): void => {
  const revisar = useRevisarPapel();
  useEffect(() => {
    if (erro) revisar(erro);
  }, [erro, revisar]);
};

/** Segmento da chave das leituras de PII completa (`obter-vida`). */
export const CHAVE_VIDA = 'vida';

/** Relê as listas e o detalhe, MAS NÃO as leituras `vida` (CPF, nascimento, nome da
 *  mãe): cada leitura dessas é um acesso a dado pessoal e deve nascer de um
 *  clique ou de uma abertura de formulário, nunca de uma invalidação ampla. */
export const useInvalidarListaVidas = (): (() => Promise<void>) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const uid = user?.id || '';
  return useCallback(() => qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'listaVidas' && q.queryKey[1] === uid && q.queryKey[2] !== CHAVE_VIDA,
  }), [qc, uid]);
};

/** Chaves do cache da Lista de vidas, sempre com o id do usuário: trocar de
 *  conta no mesmo navegador não reaproveita CPF em cache da conta anterior. */
export const useListaVidasKey = () => {
  const { user } = useAuth();
  const uid = user?.id || '';
  return useCallback((...partes: unknown[]) => ['listaVidas', uid, ...partes] as const, [uid]);
};
